import type * as React from "react";
import { Receipt } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import type { ActionFieldValue } from "../ActionFieldEditor";
import {
  VendorPreviewField,
  VendorPreviewShell,
} from "./VendorPreviewShell";
import { fmtCurrencyEUR, pickField } from "./previewUtils";

/**
 * FicCreditNotePreview — Fatture in Cloud "Nota di Credito" proposal
 * preview. Surfaces cliente + fattura riferimento + importo + ragione
 * della nota di credito so the user can verify the legal correction
 * BEFORE FiC stamps + sends it via SDI.
 */

export interface FicCreditNotePreviewProps {
  readonly proposedFields: Readonly<Record<string, ActionFieldValue>>;
  readonly executedFields?: Readonly<Record<string, ActionFieldValue>>;
}

export const FicCreditNotePreview: React.FC<FicCreditNotePreviewProps> = ({
  proposedFields,
  executedFields,
}) => {
  const { i18n } = useLingui();
  const customer =
    pickField(proposedFields, executedFields, "customer_name") ||
    pickField(proposedFields, executedFields, "client_name") ||
    pickField(proposedFields, executedFields, "destinatario");
  const refInvoice =
    pickField(proposedFields, executedFields, "reference_invoice_number") ||
    pickField(proposedFields, executedFields, "source_invoice_number");
  const refInvoiceDate = pickField(proposedFields, executedFields, "reference_invoice_date");
  const amount = executedFields?.amount ?? proposedFields.amount;
  const vat = executedFields?.vat ?? proposedFields.vat;
  const reason =
    pickField(proposedFields, executedFields, "reason") ||
    pickField(proposedFields, executedFields, "description");
  const issueDate = pickField(proposedFields, executedFields, "issue_date") ||
    pickField(proposedFields, executedFields, "date");
  const number = pickField(proposedFields, executedFields, "credit_note_number") ||
    pickField(proposedFields, executedFields, "number");

  return (
    <VendorPreviewShell
      icon={<Receipt className="h-4 w-4" />}
      title={
        number
          ? i18n._(msg`Nota di credito n. ${number}`)
          : i18n._(msg`Nota di credito`)
      }
      caption={customer ? i18n._(msg`Per ${customer}`) : "Fatture in Cloud"}
      bottomBanner={{
        label: i18n._(msg`Importo`),
        value: fmtCurrencyEUR(amount),
        tone: "danger",
      }}
      footnote={i18n._(
        msg`La nota di credito sarà registrata su Fatture in Cloud e potrà essere inviata via SDI.`,
      )}
    >
      <VendorPreviewField label={i18n._(msg`Cliente`)} value={customer} hideEmpty />
      <VendorPreviewField label={i18n._(msg`Data emissione`)} value={issueDate} hideEmpty />
      <VendorPreviewField
        label={i18n._(msg`Fattura di riferimento`)}
        value={
          refInvoice ? (
            <span className="font-mono text-[12.5px]">
              {i18n._(msg`n. ${refInvoice}`)}
              {refInvoiceDate ? ` · ${refInvoiceDate}` : ""}
            </span>
          ) : null
        }
        hideEmpty
        wide
      />
      <VendorPreviewField label={i18n._(msg`IVA`)} value={vat ? fmtCurrencyEUR(vat) : null} hideEmpty />
      <VendorPreviewField label={i18n._(msg`Motivo`)} value={reason} hideEmpty wide />
    </VendorPreviewShell>
  );
};
FicCreditNotePreview.displayName = "FicCreditNotePreview";
